import { useMutation, useQueryClient } from '@tanstack/react-query'

import { dataOrThrow, type PatchAppointmentBody } from '@snootsvet/shared/portal'

import { usePortalApi } from '@/customer/hooks/use-api'
import { availableSlotsQueryKey } from '@/customer/hooks/use-get-available-slots'
import { appointmentsQueryKey } from '@/customer/hooks/use-list-appointments'
import { captureEvent } from '@/customer/lib/posthog'

/**
 * Updates a portal appointment via `PATCH /api/portal/appointments/:id`.
 *
 * The body is a discriminated union on `action`:
 * - `reschedule`: move the appointment to a new slot (server re-verifies
 *   availability and reassigns the vet).
 * - `cancel`: cancel the appointment.
 * - `update`: amend the freeform member-facing note only.
 *
 * On success both the upcoming-appointments list and the available-slots
 * cache are invalidated - reschedule and cancel both change which slots
 * are free, so any still-mounted picker (or one the member navigates
 * back to) needs a fresh read.
 */
export function useUpdateAppointment(appointmentId: string) {
  const api = usePortalApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: PatchAppointmentBody) =>
      dataOrThrow(await api.patch('/appointments/:id', { params: { id: appointmentId }, body })),
    onSuccess: ({ appointment }, body) => {
      queryClient.invalidateQueries({ queryKey: appointmentsQueryKey })
      queryClient.invalidateQueries({ queryKey: availableSlotsQueryKey })
      if (body.action === 'update') return
      captureEvent(
        body.action === 'reschedule' ? 'appointment_rescheduled' : 'appointment_cancelled',
        {
          source: 'portal',
          appointment_id: appointment.id,
          patient_id: appointment.patientId,
          appointment_type_id: appointment.appointmentTypeId,
          clinic_id: appointment.clinicId,
        }
      )
    },
  })
}
